import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import axios from '../../../../../services/AxiosInstance';
import InvoicesTable from '../../Invoices/InvoicesTable';
import PageTitle from '../../../../layouts/PageTitle';
import { getApiUrl } from '../../../../../utils/ApiUtils';
import { COUNTRY_OPTIONS, isSweden as isSwedenCountry } from '../../../../../utils/vatTreatment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEdit,
  faEnvelope,
  faCalendarAlt
} from '@fortawesome/free-solid-svg-icons';
import { Button, Row, Col, Spinner, Alert, Modal, Form } from 'react-bootstrap';
import {
  MapPin,
  Building,
  User,
  X,
  Plus,
  Phone,
  Mail,
  Hash,
  CreditCard,
  FileText,
  ArrowLeft,
  Edit3,
  CheckCircle,
  Clock,
  AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import swal from 'sweetalert';

const ViewCustomerDetailed = () => {
  const { companyId, customerId } = useParams();
  const history = useHistory();
  const [customer, setCustomer] = useState(null);

  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({});

  const [invoices, setInvoices] = useState([]);
  const [statistics, setStatistics] = useState({
    totalEarned: 0,
    invoiceCount: 0,
    pendingInvoices: 0,
    overdueInvoices: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Modal states
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [editingAddressId, setEditingAddressId] = useState(null);
  const [editingContactId, setEditingContactId] = useState(null);
  const [addressForm, setAddressForm] = useState({
    address_type: 'billing',
    street1: '',
    street2: '',
    postalcode: '',
    city: '',
    country: 'Sverige'
  });
  const [contactForm, setContactForm] = useState({
    contact_name: '',
    role: '',
    contact_email: '',
    contact_phone: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        // Fetch customer details and invoices in parallel
        const [customerResponse, invoicesResponse] = await Promise.all([
          axios.get(getApiUrl(`${companyId}/sales/customers/${customerId}`), { withCredentials: true }),
          axios.get(getApiUrl(`${companyId}/sales/customers/${customerId}/invoices`), { withCredentials: true })
        ]);

        console.log("Print out invoice response: ", invoicesResponse);

        setCustomer(customerResponse.data);
        setInvoices(invoicesResponse.data.data);
        calculateStatistics(invoicesResponse.data.data);
      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Kunde inte hämta kunddata. Försök igen senare.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [companyId, customerId]);

  const calculateStatistics = (invoices) => {
    console.log("Invoice test:", invoices);
    const totalEarned = invoices.reduce((sum, invoice) => sum + (invoice.amount || 0), 0);
    const pendingInvoices = invoices.filter(inv => inv.status === 'pending' || inv.status === 'sent').length;

    // Calculate overdue invoices
    const today = new Date();
    const overdueInvoices = invoices.filter(invoice => {
      if (invoice.status === 'paid') return false; // Skip paid invoices

      // Check if invoice has a due date and if it's past today
      if (invoice.due_date) {
        const dueDate = new Date(invoice.due_date);
        return dueDate < today;
      }
      return false;
    }).length;

    setStatistics({
      totalEarned,
      invoiceCount: invoices.length,
      pendingInvoices,
      overdueInvoices
    });
  };

  // Open address modal for adding new address
  const handleOpenAddAddress = () => {
    setEditingAddressId(null);
    setAddressForm({
      address_type: 'billing',
      street1: '',
      street2: '',
      postalcode: '',
      city: '',
      country: 'Sverige'
    });
    setShowAddressModal(true);
  };

  // Open address modal for editing existing address
  const handleOpenEditAddress = (address) => {
    setEditingAddressId(address.address_id);
    setAddressForm({
      address_type: address.address_type || 'billing',
      street1: address.street1 || '',
      street2: address.street2 || '',
      postalcode: address.postalcode || '',
      city: address.city || '',
      country: address.country || 'Sverige'
    });
    setShowAddressModal(true);
  };

  // Handle address form submission (add or edit)
  const handleAddressSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      let response;
      if (editingAddressId) {
        // Update existing address
        response = await axios.put(
          getApiUrl(`${companyId}/sales/customers/${customerId}/addresses/${editingAddressId}`),
          addressForm,
          { withCredentials: true }
        );

        // Update customer data with updated address
        setCustomer(prev => ({
          ...prev,
          addresses: prev.addresses.map(addr =>
            addr.address_id === editingAddressId ? { ...addr, ...response.data } : addr
          )
        }));
      } else {
        // Add new address
        response = await axios.post(
          getApiUrl(`${companyId}/sales/customers/${customerId}/addresses`),
          addressForm,
          { withCredentials: true }
        );

        // Update customer data with new address
        setCustomer(prev => ({
          ...prev,
          addresses: [...(prev.addresses || []), response.data]
        }));
      }

      // Reset form and close modal
      setAddressForm({
        address_type: 'billing',
        street1: '',
        street2: '',
        postalcode: '',
        city: '',
        country: 'Sverige'
      });
      setEditingAddressId(null);
      setShowAddressModal(false);
    } catch (error) {
      toast.error(editingAddressId ? 'Kunde inte uppdatera adress. Försök igen.' : 'Kunde inte lägga till adress. Försök igen.');
    } finally {
      setSubmitting(false);
    }
  };

  // Open contact modal for adding new contact
  const handleOpenAddContact = () => {
    setEditingContactId(null);
    setContactForm({
      contact_name: '',
      role: '',
      contact_email: '',
      contact_phone: ''
    });
    setShowContactModal(true);
  };

  // Open contact modal for editing existing contact
  const handleOpenEditContact = (contact) => {
    setEditingContactId(contact.contact_id);
    setContactForm({
      contact_name: contact.contact_name || '',
      role: contact.role || '',
      contact_email: contact.contact_email || '',
      contact_phone: contact.contact_phone || ''
    });
    setShowContactModal(true);
  };

  // Handle contact form submission (add or edit)
  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      let response;
      if (editingContactId) {
        // Update existing contact
        response = await axios.put(
          getApiUrl(`${companyId}/sales/customers/${customerId}/contacts/${editingContactId}`),
          contactForm,
          { withCredentials: true }
        );

        // Update customer data with updated contact
        setCustomer(prev => ({
          ...prev,
          contacts: prev.contacts.map(c =>
            c.contact_id === editingContactId ? { ...c, ...response.data } : c
          )
        }));
      } else {
        // Add new contact
        response = await axios.post(
          getApiUrl(`${companyId}/sales/customers/${customerId}/contacts`),
          contactForm,
          { withCredentials: true }
        );

        // Update customer data with new contact
        setCustomer(prev => ({
          ...prev,
          contacts: [...(prev.contacts || []), response.data]
        }));
      }

      // Reset form and close modal
      setContactForm({
        contact_name: '',
        role: '',
        contact_email: '',
        contact_phone: ''
      });
      setEditingContactId(null);
      setShowContactModal(false);
    } catch (error) {
      toast.error(editingContactId ? 'Kunde inte uppdatera kontakt. Försök igen.' : 'Kunde inte lägga till kontaktperson. Försök igen.');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle address deletion
  const handleDeleteAddress = async (addressId) => {
    const willDelete = await swal({
      title: 'Ta bort adress',
      text: 'Är du säker på att du vill ta bort denna adress?',
      icon: 'warning',
      buttons: ['Avbryt', 'Ta bort'],
      dangerMode: true,
    });

    if (!willDelete) return;

    try {
      await axios.delete(
        getApiUrl(`${companyId}/sales/customers/${customerId}/addresses/${addressId}`),
        { withCredentials: true }
      );

      // Update customer data by removing the deleted address
      setCustomer(prev => ({
        ...prev,
        addresses: prev.addresses.filter(addr => addr.address_id !== addressId)
      }));

      toast.success('Adressen har tagits bort.');
    } catch (error) {
      toast.error('Kunde inte ta bort adressen. Försök igen.');
    }
  };

  // Handle contact deletion
  const handleDeleteContact = async (contactId) => {
    const willDelete = await swal({
      title: 'Ta bort kontaktperson',
      text: 'Är du säker på att du vill ta bort denna kontaktperson?',
      icon: 'warning',
      buttons: ['Avbryt', 'Ta bort'],
      dangerMode: true,
    });

    if (!willDelete) return;

    try {
      await axios.delete(
        getApiUrl(`${companyId}/sales/customers/${customerId}/contacts/${contactId}`),
        { withCredentials: true }
      );

      // Update customer data by removing the deleted contact
      setCustomer(prev => ({
        ...prev,
        contacts: prev.contacts.filter(c => c.contact_id !== contactId)
      }));

      toast.success('Kontaktpersonen har tagits bort.');
    } catch (error) {
      toast.error('Kunde inte ta bort kontaktpersonen. Försök igen.');
    }
  };

  const handleEditCustomer = () => {
    const country = customer.country || 'Sverige';
    setEditForm({
      type: customer.type,
      name: customer.name || '',
      first_name: customer.first_name || '',
      last_name: customer.last_name || '',
      email: customer.email || '',
      phone: customer.phone || '',
      orgnumber: customer.orgnumber || '',
      vatnumber: customer.vatnumber || '',
      country,
      // Defensive: only honor stored bygg flag when country is SE. Stale
      // data from before the form redesign could have bygg=1 set on a
      // foreign customer; we never display the conflicting state.
      is_construction_business:
        isSwedenCountry(country) &&
        Boolean(Number(customer.is_construction_business || 0)),
      payment_terms: customer.payment_terms || 30
    });
    setShowEditModal(true);
  };

  // Add this function to handle form submission
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    const country = editForm.country || 'Sverige';
    const isConstruction = isSwedenCountry(country) && Boolean(editForm.is_construction_business);
    const payload = {
      ...editForm,
      country,
      is_construction_business: isConstruction ? 1 : 0
    };

    try {
      const response = await axios.put(
        getApiUrl(`${companyId}/sales/customers/${customerId}`),
        payload,
        { withCredentials: true }
      );

      setCustomer(response.data);
      setShowEditModal(false);
      toast.success('Kunduppgifterna har uppdaterats.');
    } catch (error) {
      toast.error('Kunde inte uppdatera kunden. Försök igen.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Laddar...</span>
        </Spinner>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container-fluid">
        <Alert variant="danger" className="text-center">
          <AlertTriangle size={24} className="mb-2" />
          <h5>Ett fel uppstod</h5>
          <p>{error}</p>
          <Button variant="outline-danger" onClick={() => history.goBack()}>
            <ArrowLeft size={16} className="me-2" />
            Gå tillbaka
          </Button>
        </Alert>
      </div>
    );
  }

  if (!customer) {
    return (
      <Alert variant="warning" className="text-center">
        <h5>Kunden hittades inte</h5>
        <Button variant="outline-warning" onClick={() => history.goBack()}>
          <ArrowLeft size={16} className="me-2" />
          Gå tillbaka
        </Button>
      </Alert>
    );
  }

  const isCompany = customer.type === 'company';

  return (
    <>
      <style>{`
        .modern-card {
          background: white;
          border: none;
          border-radius: 12px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
          transition: all 0.3s ease;
          overflow: hidden;
        }

        .modern-card:hover {
          box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
        }

        .stat-card {
          background: white;
          border: none;
          border-radius: 8px;
          padding: 1.5rem;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          transition: all 0.2s ease;
        }

        .stat-card:hover {
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        .stat-icon {
          width: 48px;
          height: 48px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 1rem;
        }

        .stat-value {
          font-size: 1.75rem;
          font-weight: 700;
          margin-bottom: 0.25rem;
          line-height: 1.2;
        }

        .stat-label {
          font-size: 0.875rem;
          font-weight: 500;
          color: #374151;
          margin-bottom: 0.25rem;
        }

        .stat-subtitle {
          font-size: 0.75rem;
          color: #6b7280;
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1.25rem;
        }

        .info-item-box {
          background: #f8fafc;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          padding: 1rem;
          margin-bottom: 0.75rem;
          transition: all 0.2s ease;
        }

        .info-item-box:hover {
          background: white;
          border-color: var(--bs-primary);
          box-shadow: 0 2px 8px var(--rgba-primary-1);
        }

        .info-card {
          background: white;
          border: none;
          border-radius: 12px;
          padding: 1.5rem;
          margin-bottom: 1.5rem;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
        }

        .info-card-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1.5rem;
          padding-bottom: 1rem;
          border-bottom: 1px solid #f1f5f9;
        }

        .info-card-title {
          font-size: 1rem;
          font-weight: 600;
          color: #1e293b;
          margin: 0;
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .info-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 1.5rem;
        }

        .info-item {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .info-label {
          font-size: 0.875rem;
          font-weight: 600;
          color: #64748b;
          text-transform: uppercase;
          letter-spacing: 0.025em;
        }

        .info-value {
          font-size: 1rem;
          font-weight: 500;
          color: #1e293b;
        }

        .address-card, .contact-card {
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          padding: 1.25rem;
          margin-bottom: 1rem;
          width: 100%;
          max-width: 350px;
          position: relative;
        }

        .card-actions {
          position: absolute;
          top: 0.75rem;
          right: 0.75rem;
          display: flex;
          gap: 0.5rem;
        }

        .card-edit-button, .card-delete-button {
          background: white;
          border: 1px solid #e2e8f0;
          color: #64748b;
          width: 32px;
          height: 32px;
          border-radius: 6px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .card-edit-button:hover {
          background: #f1f5f9;
          color: var(--bs-primary);
          border-color: var(--bs-primary);
        }

        .card-delete-button:hover {
          background: #fef2f2;
          color: #dc2626;
          border-color: #fca5a5;
        }

        .address-grid, .contact-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 1rem;
          margin-bottom: 1rem;
        }

        .add-item-card {
          background: #f0fdf4;
          border: 2px dashed #16a34a;
          border-radius: 8px;
          padding: 1.25rem;
          margin-bottom: 1rem;
          width: 100%;
          max-width: 350px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s ease;
          text-align: center;
          min-height: 120px;
        }

        .add-item-card:hover {
          background: #dcfce7;
          border-color: #15803d;
          transform: translateY(-2px);
        }

        .add-item-icon {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: #16a34a;
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 0.75rem;
          font-size: 1.125rem;
        }

        .add-item-text {
          color: #16a34a;
          font-weight: 600;
          margin: 0;
        }

        .add-item-subtitle {
          color: #059669;
          font-size: 0.875rem;
          margin: 0;
          margin-top: 0.25rem;
        }

        .address-type, .contact-role {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          background: #3b82f6;
          color: white;
          padding: 0.25rem 0.75rem;
          border-radius: 6px;
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.025em;
          margin-bottom: 1rem;
        }

        .address-details, .contact-details {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .detail-line {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          font-size: 0.875rem;
          color: #374151;
        }

        .detail-icon {
          width: 16px;
          text-align: center;
          color: #64748b;
        }

        .empty-state {
          text-align: center;
          padding: 3rem 2rem;
          color: #64748b;
        }

        .empty-state-icon {
          width: 64px;
          height: 64px;
          border-radius: 50%;
          background: #f3f4f6;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 1rem;
          color: #6b7280;
        }

        @media (max-width: 1024px) {
          .stats-grid {
            grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
          }
        }

        @media (max-width: 768px) {
          .stats-grid {
            grid-template-columns: 1fr 1fr;
          }

          .info-grid {
            grid-template-columns: 1fr;
          }

          .address-grid, .contact-grid {
            grid-template-columns: 1fr;
          }

          .address-card, .contact-card, .add-item-card {
            max-width: 100%;
          }
        }
      `}</style>

      <PageTitle activeMenu={customer.name || 'Kunddetaljer'} motherMenu="Kunder" />

      <div className="col-12">
        {/* Back Button */}
        <div className="mb-3">
          <Button
            variant="link"
            className="p-0 text-decoration-none d-flex align-items-center gap-2"
            onClick={() => history.goBack()}
            style={{ color: '#64748b', fontSize: '0.875rem' }}
          >
            <ArrowLeft size={16} />
            Tillbaka till listan
          </Button>
        </div>

        {/* Header Section */}
        <div className="mb-4">
          <div className="d-flex justify-content-between align-items-start mb-3">
            <div>
              <h2 className="mb-2" style={{ fontWeight: '700', fontSize: '1.75rem' }}>
                {isCompany ? customer.name : `${customer.first_name} ${customer.last_name}`}
              </h2>
              <div className="d-flex align-items-center gap-3 flex-wrap">
                {isCompany && customer.orgnumber && (
                  <div className="d-flex align-items-center gap-2 text-muted">
                    <Hash size={16} />
                    <span style={{ fontWeight: '500', fontSize: '0.875rem' }}>
                      Org.nr: {customer.orgnumber}
                    </span>
                  </div>
                )}
                {isCompany && customer.vatnumber && (
                  <div className="d-flex align-items-center gap-2 text-muted">
                    <CreditCard size={16} />
                    <span style={{ fontWeight: '500', fontSize: '0.875rem' }}>
                      VAT: {customer.vatnumber}
                    </span>
                  </div>
                )}
                {customer.email && (
                  <div className="d-flex align-items-center gap-2 text-muted">
                    <Mail size={16} />
                    <span style={{ fontWeight: '500', fontSize: '0.875rem' }}>{customer.email}</span>
                  </div>
                )}
                {customer.phone && (
                  <div className="d-flex align-items-center gap-2 text-muted">
                    <Phone size={16} />
                    <span style={{ fontWeight: '500', fontSize: '0.875rem' }}>{customer.phone}</span>
                  </div>
                )}
              </div>
            </div>
            <Button
              variant="outline-primary"
              onClick={handleEditCustomer}
              style={{
                borderColor: 'var(--bs-primary)',
                color: 'var(--bs-primary)',
                padding: '0.5rem 1rem',
                fontWeight: '600',
                fontSize: '0.875rem'
              }}
            >
              <Edit3 size={16} className="me-2" />
              Redigera
            </Button>
          </div>
        </div>

        {/* Financial Statistics Dashboard */}
        <div className="stats-grid mb-4">
          {/* Total Invoices */}
          <div className="stat-card">
            <div className="stat-icon" style={{ background: '#f0f9ff', color: '#0284c7' }}>
              <FileText size={24} />
            </div>
            <div className="stat-value" style={{ color: '#0284c7' }}>
              {statistics.invoiceCount}
            </div>
            <div className="stat-label">Totala fakturor</div>
            <div className="stat-subtitle">
              Alla fakturor till kunden
            </div>
          </div>

          {/* Total Earned */}
          <div className="stat-card">
            <div className="stat-icon" style={{ background: '#f0fdf4', color: '#059669' }}>
              <CheckCircle size={24} />
            </div>
            <div className="stat-value" style={{ color: '#059669' }}>
              {new Intl.NumberFormat('sv-SE', {
                style: 'currency',
                currency: 'SEK',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0
              }).format(statistics.totalEarned)}
            </div>
            <div className="stat-label">Total intjänad</div>
            <div className="stat-subtitle">
              Totalt fakturerat belopp
            </div>
          </div>

          {/* Pending Invoices */}
          <div className="stat-card">
            <div className="stat-icon" style={{ background: '#fefbeb', color: '#f59e0b' }}>
              <Clock size={24} />
            </div>
            <div className="stat-value" style={{ color: '#f59e0b' }}>
              {statistics.pendingInvoices}
            </div>
            <div className="stat-label">Väntande fakturor</div>
            <div className="stat-subtitle">
              Fakturor som väntar på betalning
            </div>
          </div>

          {/* Overdue Invoices */}
          <div className="stat-card" style={statistics.overdueInvoices > 0 ? { borderLeft: '4px solid #dc2626' } : {}}>
            <div className="stat-icon" style={{ background: '#fef2f2', color: '#dc2626' }}>
              <AlertTriangle size={24} />
            </div>
            <div className="stat-value" style={{ color: '#dc2626' }}>
              {statistics.overdueInvoices}
            </div>
            <div className="stat-label">Förfallna fakturor</div>
            <div className="stat-subtitle">
              {statistics.overdueInvoices > 0 ? 'Kräver uppmärksamhet' : 'Inga förfallna fakturor'}
            </div>
          </div>
        </div>

        <Row>
          <Col lg={8}>
            {/* Basic Information */}
            <div className="info-card">
              <div className="info-card-header">
                <h3 className="info-card-title">
                  {isCompany ? <Building size={20} /> : <User size={20} />}
                  Grundläggande information
                </h3>
              </div>

              <div className="info-grid">
                {isCompany ? (
                  <>
                    <div className="info-item-box">
                      <div className="d-flex align-items-start">
                        <Building size={18} className="me-2 mt-1" style={{ color: 'var(--bs-primary)' }} />
                        <div>
                          <div style={{ fontWeight: '500', fontSize: '0.875rem' }}>{customer.name || '-'}</div>
                          <div style={{ color: '#6b7280', fontSize: '0.75rem' }}>Företagsnamn</div>
                        </div>
                      </div>
                    </div>
                    <div className="info-item-box">
                      <div className="d-flex align-items-start">
                        <Hash size={18} className="me-2 mt-1" style={{ color: 'var(--bs-primary)' }} />
                        <div>
                          <div style={{ fontWeight: '500', fontSize: '0.875rem' }}>{customer.orgnumber || '-'}</div>
                          <div style={{ color: '#6b7280', fontSize: '0.75rem' }}>Organisationsnummer</div>
                        </div>
                      </div>
                    </div>
                    <div className="info-item-box">
                      <div className="d-flex align-items-start">
                        <CreditCard size={18} className="me-2 mt-1" style={{ color: 'var(--bs-primary)' }} />
                        <div>
                          <div style={{ fontWeight: '500', fontSize: '0.875rem' }}>{customer.vatnumber || '-'}</div>
                          <div style={{ color: '#6b7280', fontSize: '0.75rem' }}>VAT-nummer</div>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="info-item-box">
                      <div className="d-flex align-items-start">
                        <User size={18} className="me-2 mt-1" style={{ color: 'var(--bs-primary)' }} />
                        <div>
                          <div style={{ fontWeight: '500', fontSize: '0.875rem' }}>{customer.first_name || '-'}</div>
                          <div style={{ color: '#6b7280', fontSize: '0.75rem' }}>Förnamn</div>
                        </div>
                      </div>
                    </div>
                    <div className="info-item-box">
                      <div className="d-flex align-items-start">
                        <User size={18} className="me-2 mt-1" style={{ color: 'var(--bs-primary)' }} />
                        <div>
                          <div style={{ fontWeight: '500', fontSize: '0.875rem' }}>{customer.last_name || '-'}</div>
                          <div style={{ color: '#6b7280', fontSize: '0.75rem' }}>Efternamn</div>
                        </div>
                      </div>
                    </div>
                  </>
                )}

                <div className="info-item-box">
                  <div className="d-flex align-items-start">
                    <Phone size={18} className="me-2 mt-1" style={{ color: 'var(--bs-primary)' }} />
                    <div>
                      <div style={{ fontWeight: '500', fontSize: '0.875rem' }}>
                        {customer.phone ? (
                          <a href={`tel:${customer.phone}`} className="text-decoration-none">
                            {customer.phone}
                          </a>
                        ) : '-'}
                      </div>
                      <div style={{ color: '#6b7280', fontSize: '0.75rem' }}>Telefonnummer</div>
                    </div>
                  </div>
                </div>

                <div className="info-item-box">
                  <div className="d-flex align-items-start">
                    <Mail size={18} className="me-2 mt-1" style={{ color: 'var(--bs-primary)' }} />
                    <div>
                      <div style={{ fontWeight: '500', fontSize: '0.875rem' }}>
                        {customer.email ? (
                          <a href={`mailto:${customer.email}`} className="text-decoration-none">
                            {customer.email}
                          </a>
                        ) : '-'}
                      </div>
                      <div style={{ color: '#6b7280', fontSize: '0.75rem' }}>E-post</div>
                    </div>
                  </div>
                </div>

                <div className="info-item-box">
                  <div className="d-flex align-items-start">
                    <Clock size={18} className="me-2 mt-1" style={{ color: 'var(--bs-primary)' }} />
                    <div>
                      <div style={{ fontWeight: '500', fontSize: '0.875rem' }}>
                        {customer.payment_terms ? `${customer.payment_terms} dagar` : '-'}
                      </div>
                      <div style={{ color: '#6b7280', fontSize: '0.75rem' }}>Betalningsvillkor</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Addresses */}
            <div className="info-card">
              <div className="info-card-header">
                <h3 className="info-card-title">
                  <MapPin size={20} />
                  Adresser
                </h3>
              </div>

              <div className="address-grid">
                {customer.addresses && customer.addresses.length > 0 && (
                  customer.addresses.map((address, index) => (
                    <div key={address.address_id || index} className="address-card">
                      <div className="card-actions">
                        <button
                          type="button"
                          className="card-edit-button"
                          onClick={() => handleOpenEditAddress(address)}
                          title="Redigera adress"
                        >
                          <Edit3 size={14} />
                        </button>
                        <button
                          type="button"
                          className="card-delete-button"
                          onClick={() => handleDeleteAddress(address.address_id)}
                          title="Ta bort adress"
                        >
                          <X size={14} />
                        </button>
                      </div>
                      <div className="address-type">
                        <MapPin size={14} />
                        {address.address_type === 'billing' ? 'Fakturaadress' :
                          address.address_type === 'shipping' ? 'Leveransadress' : 'Annan adress'}
                      </div>
                      <div className="address-details">
                        <div className="detail-line">
                          <MapPin size={14} className="detail-icon" />
                          {address.street1}{address.street2 ? `, ${address.street2}` : ''}
                        </div>
                        <div className="detail-line">
                          <span className="detail-icon"></span>
                          {address.postalcode} {address.city}
                        </div>
                        {address.country && (
                          <div className="detail-line">
                            <span className="detail-icon"></span>
                            {address.country}
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                )}

                {/* Add Address Card */}
                <div
                  className="add-item-card"
                  onClick={handleOpenAddAddress}
                >
                  <div className="add-item-icon">
                    <Plus size={18} />
                  </div>
                  <p className="add-item-text">Lägg till adress</p>
                  <p className="add-item-subtitle">Fakturaadress, leveransadress eller annan</p>
                </div>
              </div>

              {customer.addresses && customer.addresses.length === 0 && (
                <div className="empty-state">
                  <div className="empty-state-icon">
                    <MapPin size={24} />
                  </div>
                  <p className="mb-3">Inga adresser har lagts till ännu</p>
                </div>
              )}
            </div>

            {/* Contacts (only for companies) */}
            {isCompany && (
              <div className="info-card">
                <div className="info-card-header">
                  <h3 className="info-card-title">
                    <User size={20} />
                    Kontaktpersoner
                  </h3>
                </div>

                <div className="contact-grid">
                  {customer.contacts && customer.contacts.length > 0 && (
                    customer.contacts.map((contact, index) => (
                      <div key={contact.contact_id || index} className="contact-card">
                        <div className="card-actions">
                          <button
                            type="button"
                            className="card-edit-button"
                            onClick={() => handleOpenEditContact(contact)}
                            title="Redigera kontakt"
                          >
                            <Edit3 size={14} />
                          </button>
                          <button
                            type="button"
                            className="card-delete-button"
                            onClick={() => handleDeleteContact(contact.contact_id)}
                            title="Ta bort kontakt"
                          >
                            <X size={14} />
                          </button>
                        </div>
                        <div className="contact-role">
                          <User size={14} />
                          {contact.role || 'Kontaktperson'}
                        </div>
                        <div className="contact-details">
                          <div className="detail-line">
                            <User size={14} className="detail-icon" />
                            <strong>{contact.contact_name || 'Namn saknas'}</strong>
                          </div>
                          {contact.contact_email && (
                            <div className="detail-line">
                              <Mail size={14} className="detail-icon" />
                              <a href={`mailto:${contact.contact_email}`} className="text-decoration-none">
                                {contact.contact_email}
                              </a>
                            </div>
                          )}
                          {contact.contact_phone && (
                            <div className="detail-line">
                              <Phone size={14} className="detail-icon" />
                              <a href={`tel:${contact.contact_phone}`} className="text-decoration-none">
                                {contact.contact_phone}
                              </a>
                            </div>
                          )}
                        </div>
                      </div>
                    ))
                  )}

                  {/* Add Contact Card */}
                  <div
                    className="add-item-card"
                    onClick={handleOpenAddContact}
                  >
                    <div className="add-item-icon">
                      <Plus size={18} />
                    </div>
                    <p className="add-item-text">Lägg till kontaktperson</p>
                    <p className="add-item-subtitle">Ny kontakt för detta företag</p>
                  </div>
                </div>

                {customer.contacts && customer.contacts.length === 0 && (
                  <div className="empty-state">
                    <div className="empty-state-icon">
                      <User size={24} />
                    </div>
                    <p className="mb-3">Inga kontaktpersoner har lagts till ännu</p>
                  </div>
                )}
              </div>
            )}
          </Col>

          <Col lg={4}>
            {/* Quick Actions */}
            <div className="info-card">
              <div className="info-card-header">
                <h3 className="info-card-title">Snabbåtgärder</h3>
              </div>
              <div className="d-grid gap-2">
                <Button
                  variant="primary"
                  style={{
                    background: 'var(--bs-primary)',
                    borderColor: 'var(--bs-primary)',
                    padding: '0.75rem 1rem',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                  onClick={() => history.push(`/${companyId}/create-invoices?customerId=${customerId}`)}
                >
                  <FileText size={16} />
                  Skapa ny faktura
                </Button>
                <Button
                  variant="outline-secondary"
                  style={{
                    padding: '0.75rem 1rem',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                  onClick={handleEditCustomer}
                >
                  <Edit3 size={16} />
                  Redigera kunduppgifter
                </Button>
                <Button
                  variant="outline-secondary"
                  style={{
                    padding: '0.75rem 1rem',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                  onClick={handleOpenAddContact}
                >
                  <Plus size={16} />
                  Lägg till kontakt
                </Button>
              </div>
            </div>
          </Col>
        </Row>

        {/* Invoices Section */}
        <div className="modern-card">
          <div className="p-4">
            <div className="d-flex align-items-center justify-content-between mb-4">
              <div>
                <h5 style={{ margin: 0, fontWeight: '600' }}>
                  Fakturor till {isCompany ? customer.name : `${customer.first_name} ${customer.last_name}`}
                </h5>
                <p style={{ margin: 0, color: '#6b7280', fontSize: '0.875rem' }}>
                  {invoices.length > 0
                    ? `${invoices.length} fakturor och betalningshistorik`
                    : 'Inga fakturor registrerade än'
                  }
                </p>
              </div>
              <Button
                variant="primary"
                style={{
                  background: 'var(--bs-primary)',
                  borderColor: 'var(--bs-primary)',
                  padding: '0.5rem 1rem',
                  fontWeight: '600'
                }}
                onClick={() => history.push(`/${companyId}/create-invoices?customerId=${customerId}`)}
              >
                <FileText size={16} className="me-2" />
                Ny faktura
              </Button>
            </div>

            {invoices.length > 0 ? (
              <InvoicesTable invoices={invoices} companyId={companyId} />
            ) : (
              <div className="text-center py-5">
                <div
                  className="mx-auto mb-3 d-flex align-items-center justify-content-center"
                  style={{
                    width: '64px',
                    height: '64px',
                    borderRadius: '50%',
                    background: '#f3f4f6',
                    color: '#6b7280'
                  }}
                >
                  <FileText size={24} />
                </div>
                <h6 style={{ color: '#374151', marginBottom: '0.5rem' }}>Inga fakturor än</h6>
                <p style={{ color: '#6b7280', fontSize: '0.875rem', marginBottom: '1.5rem' }}>
                  Det finns inga registrerade fakturor till denna kund.
                </p>
                <Button
                  variant="outline-primary"
                  style={{
                    borderColor: 'var(--bs-primary)',
                    color: 'var(--bs-primary)',
                    padding: '0.5rem 1rem'
                  }}
                  onClick={() => history.push(`/${companyId}/create-invoices?customerId=${customerId}`)}
                >
                  <FileText size={16} className="me-2" />
                  Skapa första fakturan
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add Address Modal */}
      <>
        <style>{`
          .address-modal-content {
            border-radius: 8px;
            border: none;
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
            overflow: hidden;
          }
          
          .address-modal-header {
            background: white;
            color: #1e293b;
            padding: 1.5rem 2rem;
            border-bottom: 1px solid #e2e8f0;
            position: relative;
          }
          
          .address-modal-body {
            padding: 2rem;
            background: #ffffff;
            max-height: 70vh;
            overflow-y: auto;
          }
          
          .address-section-card {
            background: transparent;
            border: none;
            padding: 0;
            margin-bottom: 2rem;
          }
          
          .address-section-header {
            display: flex;
            align-items: center;
            gap: 0.75rem;
            margin-bottom: 1.5rem;
            padding: 0;
            border: none;
            background: transparent;
          }
          
          .address-section-title {
            font-size: 1.125rem;
            font-weight: 600;
            color: #1e293b;
            margin: 0;
          }
          
          .address-section-subtitle {
            font-size: 0.875rem;
            color: #64748b;
            margin: 0;
          }
          
          .address-type-selector {
            display: grid;
            grid-template-columns: 1fr 1fr 1fr;
            gap: 1rem;
            margin-bottom: 2rem;
          }
          
          .address-type-option {
            border: 2px solid #e2e8f0;
            border-radius: 8px;
            padding: 1.5rem;
            text-align: center;
            cursor: pointer;
            transition: all 0.2s ease;
            background: white;
          }
          
          .address-type-option:hover {
            border-color: #cbd5e1;
            background: #f8fafc;
          }
          
          .address-type-option.selected {
            border-color: var(--bs-primary);
            background: #eff7f1ff;
          }
          
          .address-type-icon {
            width: 48px;
            height: 48px;
            border-radius: 8px;
            display: flex;
            align-items: center;
            justify-content: center;
            margin: 0 auto 0.75rem;
            background: #f1f5f9;
            color: #64748b;
            transition: all 0.2s ease;
          }
          
          .address-type-option.selected .address-type-icon {
            background: var(--bs-primary);
            color: white;
          }
          
          .address-type-label {
            font-weight: 600;
            color: #1e293b;
            margin-bottom: 0.25rem;
          }
          
          .address-type-description {
            font-size: 0.875rem;
            color: #64748b;
          }
          
          .address-form-group {
            margin-bottom: 1.5rem;
          }
          
          .address-form-label {
            display: block;
            font-size: 0.875rem;
            font-weight: 500;
            color: #374151;
            margin-bottom: 0.5rem;
          }
          
          .address-form-label.required::after {
            content: " *";
            color: #ef4444;
          }
          
          .address-form-input {
            width: 100%;
            padding: 0.75rem 1rem;
            border: 1px solid #d1d5db;
            border-radius: 6px;
            font-size: 0.875rem;
            transition: all 0.2s ease;
          }
          
          .address-form-input:focus {
            outline: none;
            border-color: var(--bs-primary);
            box-shadow: 0 0 0 2px rgba(22, 107, 72, 0.1);
          }
          
          .address-form-grid {
            display: grid;
            gap: 1rem;
          }
          
          .address-grid-2 {
            grid-template-columns: 1fr 1fr;
          }
          
          .address-grid-3 {
            grid-template-columns: 1fr 1fr 1fr;
          }
          
          .address-modal-actions {
            background: #f8fafc;
            padding: 1.5rem 2rem;
            border-top: 1px solid #e2e8f0;
            display: flex;
            justify-content: space-between;
            align-items: center;
          }
          
          .address-action-button {
            border-radius: 6px;
            padding: 0.75rem 1.5rem;
            font-weight: 500;
            font-size: 0.875rem;
            border: none;
            transition: all 0.2s ease;
            display: flex;
            align-items: center;
            gap: 0.5rem;
          }
          
          .address-btn-cancel {
            background: #f1f5f9;
            color: #64748b;
            border: 1px solid #e2e8f0;
          }
          
          .address-btn-cancel:hover {
            background: #e2e8f0;
            color: #475569;
          }
          
          .address-btn-save {
            background: var(--bs-primary);
            color: white;
          }
          
          .address-btn-save:hover {
            background: var(--bs-primary);
          }
          
          .address-close-button {
            position: absolute;
            top: 1rem;
            right: 1.5rem;
            background: #f1f5f9;
            border: 1px solid #e2e8f0;
            color: #64748b;
            width: 32px;
            height: 32px;
            border-radius: 4px;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: all 0.2s ease;
          }
          
          .address-close-button:hover {
            background: #e2e8f0;
            color: #475569;
          }
          
          .address-loading-spinner {
            width: 16px;
            height: 16px;
            border: 2px solid rgba(59, 130, 246, 0.3);
            border-top: 2px solid #3b82f6;
            border-radius: 50%;
            animation: spin 1s linear infinite;
          }
          
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          
          @media (max-width: 768px) {
            .address-type-selector {
              grid-template-columns: 1fr;
            }
            
            .address-grid-2, .address-grid-3 {
              grid-template-columns: 1fr;
            }
          }
        `}</style>

        <Modal
          className='fade'
          size='lg'
          show={showAddressModal}
          onHide={() => { setShowAddressModal(false); setEditingAddressId(null); }}
          backdrop="static"
          centered
        >
          <div className="address-modal-content">
            <Form onSubmit={handleAddressSubmit}>
              {/* Header */}
              <div className="address-modal-header">
                <button type="button" className="address-close-button" onClick={() => { setShowAddressModal(false); setEditingAddressId(null); }}>
                  <X size={16} />
                </button>
                <div className="d-flex align-items-center gap-3">
                  <MapPin size={24} />
                  <div>
                    <h3 className="mb-1" style={{ fontSize: '1.25rem', fontWeight: '600' }}>
                      {editingAddressId ? 'Redigera adress' : 'Lägg till adress'}
                    </h3>
                    <p className="mb-0" style={{ color: '#64748b', fontSize: '0.875rem' }}>
                      {editingAddressId ? 'Uppdatera adressuppgifter' : 'Skapa en ny adress för kunden'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Body */}
              <div className="address-modal-body">
                {/* Address Type Selection */}
                <div className="address-section-card">
                  <div className="address-section-header">
                    <Building size={20} />
                    <div>
                      <h4 className="address-section-title">Adresstyp</h4>
                      <p className="address-section-subtitle">Välj typ av adress som ska läggas till</p>
                    </div>
                  </div>

                  <div className="address-type-selector">
                    <div
                      className={`address-type-option ${addressForm.address_type === 'billing' ? 'selected' : ''}`}
                      onClick={() => setAddressForm({ ...addressForm, address_type: 'billing' })}
                    >
                      <div className="address-type-icon">
                        <FileText size={24} />
                      </div>
                      <div className="address-type-label">Fakturaadress</div>
                      <div className="address-type-description">För fakturering och korrespondens</div>
                    </div>

                    <div
                      className={`address-type-option ${addressForm.address_type === 'shipping' ? 'selected' : ''}`}
                      onClick={() => setAddressForm({ ...addressForm, address_type: 'shipping' })}
                    >
                      <div className="address-type-icon">
                        <MapPin size={24} />
                      </div>
                      <div className="address-type-label">Leveransadress</div>
                      <div className="address-type-description">För leveranser och frakt</div>
                    </div>

                    <div
                      className={`address-type-option ${addressForm.address_type === 'other' ? 'selected' : ''}`}
                      onClick={() => setAddressForm({ ...addressForm, address_type: 'other' })}
                    >
                      <div className="address-type-icon">
                        <Building size={24} />
                      </div>
                      <div className="address-type-label">Annan adress</div>
                      <div className="address-type-description">Övrig adressinformation</div>
                    </div>
                  </div>
                </div>

                {/* Address Information */}
                <div className="address-section-card">
                  <div className="address-section-header">
                    <MapPin size={20} />
                    <div>
                      <h4 className="address-section-title">Adressinformation</h4>
                      <p className="address-section-subtitle">Fyll i adressdetaljerna</p>
                    </div>
                  </div>

                  <div className="address-form-group">
                    <label className="address-form-label required">Gatuadress</label>
                    <input
                      type="text"
                      className="address-form-input"
                      value={addressForm.street1}
                      onChange={(e) => setAddressForm({ ...addressForm, street1: e.target.value })}
                      placeholder="Gatunamn och nummer"
                      required
                    />
                  </div>

                  <div className="address-form-group">
                    <label className="address-form-label">Gatuadress 2 (valfritt)</label>
                    <input
                      type="text"
                      className="address-form-input"
                      value={addressForm.street2}
                      onChange={(e) => setAddressForm({ ...addressForm, street2: e.target.value })}
                      placeholder="Lägenhetsnummer, våning, etc."
                    />
                  </div>

                  <div className="address-form-grid address-grid-3">
                    <div className="address-form-group">
                      <label className="address-form-label required">Ort</label>
                      <input
                        type="text"
                        className="address-form-input"
                        value={addressForm.city}
                        onChange={(e) => setAddressForm({ ...addressForm, city: e.target.value })}
                        placeholder="Stockholm"
                        required
                      />
                    </div>

                    <div className="address-form-group">
                      <label className="address-form-label required">Postnummer</label>
                      <input
                        type="text"
                        className="address-form-input"
                        value={addressForm.postalcode}
                        onChange={(e) => setAddressForm({ ...addressForm, postalcode: e.target.value })}
                        placeholder="XXX XX"
                        required
                      />
                    </div>

                    <div className="address-form-group">
                      <label className="address-form-label">Land</label>
                      <input
                        type="text"
                        className="address-form-input"
                        value={addressForm.country}
                        onChange={(e) => setAddressForm({ ...addressForm, country: e.target.value })}
                        placeholder="Sverige"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="address-modal-actions">
                <div style={{ fontSize: '0.875rem', color: '#64748b' }}>
                  Alla obligatoriska fält måste fyllas i
                </div>
                <div className="d-flex gap-3">
                  <button
                    type="button"
                    className="address-action-button address-btn-cancel"
                    onClick={() => { setShowAddressModal(false); setEditingAddressId(null); }}
                  >
                    Avbryt
                  </button>
                  <button
                    type="submit"
                    className="address-action-button address-btn-save"
                    disabled={submitting}
                  >
                    {submitting ? (
                      <>
                        <div className="address-loading-spinner"></div>
                        Sparar...
                      </>
                    ) : (
                      <>
                        {editingAddressId ? <FontAwesomeIcon icon={faEdit} /> : <Plus size={16} />}
                        {editingAddressId ? 'Spara ändringar' : 'Lägg till adress'}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </Form>
          </div>
        </Modal>
      </>

      {/* Contact Modal */}
      <Modal
        className='fade'
        size='lg'
        show={showContactModal}
        onHide={() => { setShowContactModal(false); setEditingContactId(null); }}
        backdrop="static"
        centered
      >
        <div className="address-modal-content">
          <Form onSubmit={handleContactSubmit}>
            {/* Header */}
            <div className="address-modal-header">
              <button type="button" className="address-close-button" onClick={() => { setShowContactModal(false); setEditingContactId(null); }}>
                <X size={16} />
              </button>
              <div className="d-flex align-items-center gap-3">
                <User size={24} />
                <div>
                  <h3 className="mb-1" style={{ fontSize: '1.25rem', fontWeight: '600' }}>
                    {editingContactId ? 'Redigera kontaktperson' : 'Lägg till kontaktperson'}
                  </h3>
                  <p className="mb-0" style={{ color: '#64748b', fontSize: '0.875rem' }}>
                    {editingContactId ? 'Uppdatera kontaktpersonens uppgifter' : 'Skapa en ny kontaktperson för kunden'}
                  </p>
                </div>
              </div>
            </div>

            {/* Body */}
            <div className="address-modal-body">
              {/* Contact Information */}
              <div className="address-section-card">
                <div className="address-section-header">
                  <User size={20} />
                  <div>
                    <h4 className="address-section-title">Kontaktinformation</h4>
                    <p className="address-section-subtitle">Fyll i kontaktpersonens uppgifter</p>
                  </div>
                </div>

                <div className="address-form-group">
                  <label className="address-form-label required">Namn</label>
                  <input
                    type="text"
                    className="address-form-input"
                    value={contactForm.contact_name}
                    onChange={(e) => setContactForm({ ...contactForm, contact_name: e.target.value })}
                    placeholder="För- och efternamn"
                    required
                  />
                </div>

                <div className="address-form-group">
                  <label className="address-form-label">Roll/Titel</label>
                  <input
                    type="text"
                    className="address-form-input"
                    value={contactForm.role}
                    onChange={(e) => setContactForm({ ...contactForm, role: e.target.value })}
                    placeholder="t.ex. VD, Ekonomichef, Inköpsansvarig"
                  />
                </div>

                <div className="address-form-grid address-grid-2">
                  <div className="address-form-group">
                    <label className="address-form-label">E-postadress</label>
                    <input
                      type="email"
                      className="address-form-input"
                      value={contactForm.contact_email}
                      onChange={(e) => setContactForm({ ...contactForm, contact_email: e.target.value })}
                      placeholder="namn@företag.se"
                    />
                  </div>

                  <div className="address-form-group">
                    <label className="address-form-label">Telefonnummer</label>
                    <input
                      type="tel"
                      className="address-form-input"
                      value={contactForm.contact_phone}
                      onChange={(e) => setContactForm({ ...contactForm, contact_phone: e.target.value })}
                      placeholder="08-123 456 78"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="address-modal-actions">
              <div style={{ fontSize: '0.875rem', color: '#64748b' }}>
                Alla obligatoriska fält måste fyllas i
              </div>
              <div className="d-flex gap-3">
                <button
                  type="button"
                  className="address-action-button address-btn-cancel"
                  onClick={() => { setShowContactModal(false); setEditingContactId(null); }}
                >
                  Avbryt
                </button>
                <button
                  type="submit"
                  className="address-action-button address-btn-save"
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <div className="address-loading-spinner"></div>
                      Sparar...
                    </>
                  ) : (
                    <>
                      {editingContactId ? <FontAwesomeIcon icon={faEdit} /> : <Plus size={16} />}
                      {editingContactId ? 'Spara ändringar' : 'Lägg till kontakt'}
                    </>
                  )}
                </button>
              </div>
            </div>
          </Form>
        </div>
      </Modal>

      <Modal
        className='fade'
        size='lg'
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        backdrop="static"
        centered
      >
        <div className="address-modal-content">
          <Form onSubmit={handleEditSubmit}>
            {/* Header */}
            <div className="address-modal-header">
              <button type="button" className="address-close-button" onClick={() => setShowEditModal(false)}>
                <X size={16} />
              </button>
              <div className="d-flex align-items-center gap-3">
                {isCompany ? <Building size={24} /> : <User size={24} />}
                <div>
                  <h3 className="mb-1" style={{ fontSize: '1.25rem', fontWeight: '600' }}>
                    Redigera kund
                  </h3>
                  <p className="mb-0" style={{ color: '#64748b', fontSize: '0.875rem' }}>
                    Uppdatera kundens uppgifter
                  </p>
                </div>
              </div>
            </div>

            {/* Body */}
            <div className="address-modal-body">
              {/* Company/Individual Information */}
              <div className="address-section-card">
                <div className="address-section-header">
                  {editForm.type === 'company' ? <Building size={20} /> : <User size={20} />}
                  <div>
                    <h4 className="address-section-title">
                      {editForm.type === 'company' ? 'Företagsinformation' : 'Personuppgifter'}
                    </h4>
                    <p className="address-section-subtitle">
                      {editForm.type === 'company' ? 'Grundläggande företagsuppgifter' : 'Grundläggande personuppgifter'}
                    </p>
                  </div>
                </div>

                {editForm.type === 'company' ? (
                  <>
                    <div className="address-form-group">
                      <label className="address-form-label required">Företagsnamn</label>
                      <input
                        type="text"
                        className="address-form-input"
                        value={editForm.name}
                        onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                        required
                      />
                    </div>

                    <div className="address-form-grid address-grid-2">
                      <div className="address-form-group">
                        <label className="address-form-label">Organisationsnummer</label>
                        <input
                          type="text"
                          className="address-form-input"
                          value={editForm.orgnumber}
                          onChange={(e) => setEditForm({ ...editForm, orgnumber: e.target.value })}
                        />
                      </div>

                      <div className="address-form-group">
                        <label className="address-form-label">VAT-nummer</label>
                        <input
                          type="text"
                          className="address-form-input"
                          value={editForm.vatnumber}
                          onChange={(e) => setEditForm({ ...editForm, vatnumber: e.target.value })}
                        />
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="address-form-grid address-grid-2">
                    <div className="address-form-group">
                      <label className="address-form-label required">Förnamn</label>
                      <input
                        type="text"
                        className="address-form-input"
                        value={editForm.first_name}
                        onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })}
                        required
                      />
                    </div>

                    <div className="address-form-group">
                      <label className="address-form-label required">Efternamn</label>
                      <input
                        type="text"
                        className="address-form-input"
                        value={editForm.last_name}
                        onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })}
                        required
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Contact Information */}
              <div className="address-section-card">
                <div className="address-section-header">
                  <FontAwesomeIcon icon={faEnvelope} />
                  <div>
                    <h4 className="address-section-title">Kontaktuppgifter</h4>
                    <p className="address-section-subtitle">E-post och telefon</p>
                  </div>
                </div>

                <div className="address-form-grid address-grid-2">
                  <div className="address-form-group">
                    <label className="address-form-label">E-post</label>
                    <input
                      type="email"
                      className="address-form-input"
                      value={editForm.email}
                      onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                      placeholder="namn@företag.se"
                    />
                  </div>

                  <div className="address-form-group">
                    <label className="address-form-label">Telefon</label>
                    <input
                      type="tel"
                      className="address-form-input"
                      value={editForm.phone}
                      onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                      placeholder="08-123 456 78"
                    />
                  </div>
                </div>
              </div>

              {/* Payment Terms */}
              <div className="address-section-card">
                <div className="address-section-header">
                  <FontAwesomeIcon icon={faCalendarAlt} />
                  <div>
                    <h4 className="address-section-title">Betalningsvillkor</h4>
                    <p className="address-section-subtitle">Fakturerings- och betalningsinställningar</p>
                  </div>
                </div>

                <div className="address-form-group" style={{ maxWidth: '200px' }}>
                  <label className="address-form-label">Betalningsvillkor (dagar)</label>
                  <input
                    type="number"
                    className="address-form-input"
                    value={editForm.payment_terms}
                    onChange={(e) => setEditForm({ ...editForm, payment_terms: parseInt(e.target.value) })}
                    min="1"
                    max="365"
                  />
                </div>
              </div>

              {/* Land (Session C). Same disable-on-conflict pattern as
                  NewCustomerModal. */}
              <div className="address-section-card">
                <div className="address-section-header">
                  <MapPin size={20} />
                  <div>
                    <h4 className="address-section-title">Land</h4>
                    <p className="address-section-subtitle">
                      Land där kunden är registrerad. Avgör om fakturan bär svensk moms, omvänd skattskyldighet (EU) eller faller utanför momsens tillämpningsområde.
                    </p>
                  </div>
                </div>
                <div className="address-form-group" style={{ maxWidth: '300px' }}>
                  <label className="address-form-label required">Land</label>
                  <select
                    className="address-form-input"
                    value={editForm.country || 'Sverige'}
                    onChange={(e) => {
                      const next = e.target.value;
                      const update = { ...editForm, country: next };
                      // Auto-clear bygg flag when leaving Sverige.
                      if (!isSwedenCountry(next) && editForm.is_construction_business) {
                        update.is_construction_business = false;
                      }
                      setEditForm(update);
                    }}
                  >
                    <optgroup label="Sverige">
                      {COUNTRY_OPTIONS.filter(c => c.group === 'Sverige').map(c => (
                        <option key={c.value} value={c.value}>{c.label}</option>
                      ))}
                    </optgroup>
                    <optgroup label="EU-länder">
                      {COUNTRY_OPTIONS.filter(c => c.group === 'EU').map(c => (
                        <option key={c.value} value={c.value}>{c.label}</option>
                      ))}
                    </optgroup>
                    <optgroup label="Övriga länder">
                      {COUNTRY_OPTIONS.filter(c => c.group === 'Övriga').map(c => (
                        <option key={c.value} value={c.value}>{c.label}</option>
                      ))}
                    </optgroup>
                  </select>
                </div>
              </div>

              {/* Omvänd skattskyldighet — disabled when country !== Sverige.
                  ML 16 kap 13 § applies between two SE construction
                  businesses; meaningless for foreign customers. */}
              {(() => {
                const byggDisabled = !isSwedenCountry(editForm.country || 'Sverige');
                return (
                  <div className="address-section-card">
                    <div className="address-section-header">
                      <Building size={20} />
                      <div>
                        <h4 className="address-section-title">Omvänd skattskyldighet</h4>
                        <p className="address-section-subtitle">
                          Markera om kunden är ett byggföretag och omfattas av omvänd skattskyldighet inom byggsektorn (ML 16 kap 13 §).
                        </p>
                      </div>
                    </div>
                    <div className="address-form-group">
                      <label
                        className="address-form-label"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          marginBottom: 0,
                          opacity: byggDisabled ? 0.5 : 1,
                          cursor: byggDisabled ? 'not-allowed' : 'pointer'
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={Boolean(editForm.is_construction_business) && !byggDisabled}
                          disabled={byggDisabled}
                          onChange={(e) => setEditForm({ ...editForm, is_construction_business: e.target.checked })}
                          style={{ width: 'auto' }}
                        />
                        Byggföretag — omvänd skattskyldighet inom byggsektorn
                      </label>
                      {byggDisabled && (
                        <div style={{ fontSize: '0.8125rem', color: '#94a3b8', marginTop: '0.25rem' }}>
                          Endast tillämpligt för svenska kunder. För utländska kunder hanteras moms via Land ovan (ML 6 kap eller utanför momsens tillämpningsområde).
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Actions */}
            <div className="address-modal-actions">
              <div style={{ fontSize: '0.875rem', color: '#64748b' }}>
                Alla obligatoriska fält måste fyllas i
              </div>
              <div className="d-flex gap-3">
                <button
                  type="button"
                  className="address-action-button address-btn-cancel"
                  onClick={() => setShowEditModal(false)}
                >
                  Avbryt
                </button>
                <button
                  type="submit"
                  className="address-action-button address-btn-save"
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <div className="address-loading-spinner"></div>
                      Sparar...
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faEdit} />
                      Spara ändringar
                    </>
                  )}
                </button>
              </div>
            </div>
          </Form>
        </div>
      </Modal>
    </>
  );
};

export default ViewCustomerDetailed;
